/**
 * Los borradores de captura y su bajada al payload wire (docs/esquema.md §5.2,
 * protocolo 1 — v3). La UI habla en KILOS; el wire en GRAMOS enteros — la
 * conversión vive SOLO acá (§3 del esquema). Los ids (registro, mutación) se
 * generan UNA vez al crear el borrador y sobreviven a cierres y reintentos.
 *
 * v3: `fecha` NO viaja (la deriva el servidor de `entrada_en`) y no hay foto. El
 * descarte de recepción es el otro registro de la estación verde: quién, kilos
 * netos, cuándo.
 */

import type { BorradorProduccion } from './produccion';
import type { BorradorEmbolsado } from './embolsado';
import type { BorradorMuestra } from './muestra';
import { instanteLocal } from './tiempo';

/**
 * Cuántos carros entran en un viaje, en la pantalla.
 *
 * `[DATO]`: en los 270 viajes de la temporada 2025, 112 trajeron un carro y 158
 * trajeron dos. Ninguno trajo tres. La planta tiene cuatro carros y se rotan,
 * pero el camión entra con uno o con dos, así que un tercer toque en la grilla
 * es un dedazo y no un viaje real (visto en la planta el 9/9/2026).
 *
 * El servidor sigue aceptando hasta `MAX_CARROS_POR_VIAJE`: este tope es de la
 * pantalla, para que el error no llegue a cargarse. Si algún día entra un viaje
 * de tres de verdad, se corrige desde el tablero y el dato no se pierde.
 */
export const CARROS_POR_VIAJE = 2;

/** Snapshot CONGELADO al elegir el carro: un refresh de catálogos no lo cambia. */
export interface CarroElegido {
	carro_id: string;
	carro_tara_id: string;
	tara_g_snapshot: number;
	numero: number;
	color: string | null;
}

export interface Borrador {
	tipo: 'viaje';
	capture_id: string;
	viaje_id: string;
	mutacion_id: string;
	/** La campaña se fija al CREAR el borrador: un cambio de campaña o de listas no lo mueve. */
	campania_id: string | null;
	paso: number;
	personas: string[];
	lote_id: string | null;
	carros: CarroElegido[];
	corte_ms: number | null;
	entrada_ms: number | null;
	bruto_kg: number | null;
	/**
	 * Obsoleto desde el 6/10 (Agata: un bruto raro avisa y deja seguir, ya no hay «Sí, está
	 * bien»): queda en el tipo porque un borrador guardado antes puede traerlo; nadie lo lee.
	 */
	bruto_forzado?: boolean;
	nota: string | null;
	actualizado_en: number;
}

/** El descarte de recepción: lo pesa una persona en la balanza pública, ya neto. */
export interface BorradorDescarte {
	tipo: 'descarte';
	capture_id: string;
	descarte_id: string;
	mutacion_id: string;
	campania_id: string | null;
	/** v6 (§12.8): 0 = quién, 1 = carros de descarte, 2 = bruto, 3 = revisar. */
	paso: number;
	personas: string[];
	/** Antes de la v6 se tipeaba el neto: un borrador viejo lo trae y se ignora (se vuelve a pesar). */
	neto_kg?: number | null;
	/** v6 (§12.8): el bruto de la balanza y los carros de descarte con su tara congelada. Opcionales: un borrador viejo no los trae. */
	bruto_kg?: number | null;
	carros?: CarroDeDescarteElegido[];
	registrado_ms: number | null;
	nota: string | null;
	actualizado_en: number;
}

/**
 * v6 (§12.8): un carro elegido para el descarte, con la tara aplicable congelada al tocarlo. Es uno
 * de descarte o, a veces, uno de verde (2/10): dos listas con numeración propia, cada una con su tara.
 */
export type CarroDeDescarteElegido = { tara_g_snapshot: number; numero: number; color: string | null } & (
	| { carro_descarte_id: string; carro_descarte_tara_id: string }
	| { carro_id: string; carro_tara_id: string }
);

/** ¿Es de los carros de verde? (los de descarte llevan `carro_descarte_id`). */
export const esCarroDeVerde = (c: CarroDeDescarteElegido): c is CarroDeDescarteElegido & { carro_id: string; carro_tara_id: string } => 'carro_id' in c;

/** El nombre de un carro del descarte: «Nº 3», o «verde Nº 3» si es de los de verde. */
export const nombreDeCarroDeDescarte = (c: CarroDeDescarteElegido): string => (esCarroDeVerde(c) ? `verde Nº ${c.numero}` : `Nº ${c.numero}`);

/** El neto del descarte: el bruto menos las taras de sus carros (null sin bruto o sin carros). */
export function netoDelDescarteG(borrador: Pick<BorradorDescarte, 'bruto_kg' | 'carros'>): number | null {
	if (!borrador.bruto_kg || !borrador.carros || borrador.carros.length === 0) return null;
	return borrador.bruto_kg * 1000 - borrador.carros.reduce((suma, c) => suma + c.tara_g_snapshot, 0);
}

/**
 * ¿Terminó de tipear el bruto? Mientras el número tiene menos cifras que los carros vacíos todavía
 * puede crecer, y el cartel de «es menos que los carros» sería ruido (2/10: saltaba desde el primer
 * dígito). Es la regla de los cuatro dígitos del viaje, con el piso que ponen los carros elegidos.
 * Solo gobierna el cartel: el botón sigue trabado mientras el neto no dé positivo.
 */
export function terminoDeTipearElDescarte(borrador: Pick<BorradorDescarte, 'bruto_kg' | 'carros'>): boolean {
	if (!borrador.bruto_kg) return false;
	const tarasKg = Math.ceil((borrador.carros ?? []).reduce((suma, c) => suma + c.tara_g_snapshot, 0) / 1000);
	return String(borrador.bruto_kg).length >= String(tarasKg).length;
}

export type BorradorCualquiera = Borrador | BorradorDescarte | BorradorProduccion | BorradorMuestra | BorradorEmbolsado;

/**
 * Qué hace tocar un nombre en la grilla de personas.
 *
 * **El dueño del teléfono no se deselecciona.** El teléfono es personal y no se
 * presta: si está cargando, esa persona está trabajando, así que sacarla de la
 * lista no describe ninguna situación real — y tocar el propio nombre premarcado
 * dejaba el registro sin nadie (visto en la planta el 9/9/2026). Lo que sí se
 * toca es quién más: el compañero se agrega y se saca libremente.
 *
 * Con `tope` 1, tocar otro nombre REEMPLAZA al elegido, dueño incluido: en
 * producción el copy dice «si hoy carga otra, tocá su nombre», y eso tiene que
 * funcionar. Lo que NUNCA queda es vacío: con una sola elegida, tocarla no la
 * saca — un registro sin nadie no describe nada y solo apaga el botón de seguir.
 *
 * Muta el array recibido, que es el del borrador, y lo devuelve.
 */
export function alternarPersona(elegidas: string[], personaId: string, tope: number, duenio: string | null = null): string[] {
	const posicion = elegidas.indexOf(personaId);
	if (posicion >= 0) {
		if (personaId !== duenio && elegidas.length > 1) elegidas.splice(posicion, 1);
	} else if (elegidas.length < tope) {
		elegidas.push(personaId);
	} else if (tope === 1) {
		elegidas.splice(0, 1, personaId);
	}
	return elegidas;
}

export function sumaTarasG(borrador: Pick<Borrador, 'carros'>): number {
	return borrador.carros.reduce((suma, carro) => suma + carro.tara_g_snapshot, 0);
}

/** Bruto del viaje en gramos: la balanza pesa el camión con sus carros, y eso es el viaje. */
export function brutoG(borrador: Pick<Borrador, 'bruto_kg'>): number {
	return (borrador.bruto_kg ?? 0) * 1000;
}

export interface PayloadCrearWire {
	viaje_id: string;
	campania_id: string;
	lote_campo_id: string;
	corte_en: string | null;
	entrada_en: string;
	bruto_g: number;
	notas: string | null;
	persona_id: string;
	persona2_id: string | null;
	carros: Array<{ carro_id: string; carro_tara_id: string; tara_g_snapshot: number }>;
}

/**
 * Borrador completo → payload wire. Tira si falta algo obligatorio (la UI no
 * deja llegar acá incompleto; esto es el cinturón).
 */
export function armarPayloadCrear(borrador: Borrador, campaniaId: string): PayloadCrearWire {
	if (!borrador.lote_id || borrador.personas.length === 0 || borrador.carros.length === 0 || borrador.entrada_ms === null) {
		throw new Error('borrador incompleto');
	}
	const notas = borrador.nota && borrador.nota.length > 0 ? borrador.nota : null;
	return {
		viaje_id: borrador.viaje_id,
		campania_id: campaniaId,
		lote_campo_id: borrador.lote_id,
		corte_en: borrador.corte_ms === null ? null : instanteLocal(borrador.corte_ms),
		entrada_en: instanteLocal(borrador.entrada_ms),
		bruto_g: brutoG(borrador),
		notas,
		persona_id: borrador.personas[0],
		persona2_id: borrador.personas[1] ?? null,
		carros: borrador.carros.map((c) => ({ carro_id: c.carro_id, carro_tara_id: c.carro_tara_id, tara_g_snapshot: c.tara_g_snapshot })),
	};
}

/** v6 (§12.8): viaja el bruto y los carros; el neto lo escribe el servidor. */
export interface PayloadDescarteWire {
	descarte_id: string;
	campania_id: string;
	registrado_en: string;
	bruto_g: number;
	carros: Array<{ carro_descarte_id: string; carro_descarte_tara_id: string; tara_g_snapshot: number } | { carro_id: string; carro_tara_id: string; tara_g_snapshot: number }>;
	notas: string | null;
	persona_id: string;
	/** 0004 (§12.13): la segunda persona que lo lleva, como en el viaje. */
	persona2_id: string | null;
}

export function armarPayloadDescarte(borrador: BorradorDescarte, campaniaId: string): PayloadDescarteWire {
	const neto = netoDelDescarteG(borrador);
	if (borrador.personas.length === 0 || !borrador.bruto_kg || !borrador.carros || neto === null || neto <= 0 || borrador.registrado_ms === null) {
		throw new Error('borrador incompleto');
	}
	return {
		descarte_id: borrador.descarte_id,
		campania_id: campaniaId,
		registrado_en: instanteLocal(borrador.registrado_ms),
		bruto_g: borrador.bruto_kg * 1000,
		carros: borrador.carros.map((c) =>
			esCarroDeVerde(c)
				? { carro_id: c.carro_id, carro_tara_id: c.carro_tara_id, tara_g_snapshot: c.tara_g_snapshot }
				: { carro_descarte_id: c.carro_descarte_id, carro_descarte_tara_id: c.carro_descarte_tara_id, tara_g_snapshot: c.tara_g_snapshot },
		),
		notas: borrador.nota && borrador.nota.length > 0 ? borrador.nota : null,
		persona_id: borrador.personas[0],
		persona2_id: borrador.personas[1] ?? null,
	};
}

/**
 * v6 (§12.3): ¿el carro figura sin bajar? Un viaje de este teléfono que todavía no se refleja en el
 * catálogo y lo trajo manda (nadie pudo haberlo bajado después sin que el catálogo lo sepa); si no,
 * el catálogo `carros_en_playa`. Devuelve el viaje desde el que no figura bajado, o null. Avisa: no
 * bloquea (23 de 208 llegadas de 2026 fueron bajadas que no se anotaron).
 */
export function carroSinBajar(
	carroId: string,
	enPlaya: Array<{ carro_id: string; nro_viaje: number | null; entrada_en: string }>,
	viajesLocales: Array<{ tipo: string; payload: unknown }>,
): { nro_viaje: number | null; local: boolean } | null {
	const local = viajesLocales.some((m) => {
		if (m.tipo !== 'viaje_verde.crear') return false;
		const carros = (m.payload as { carros?: Array<{ carro_id?: unknown }> } | null)?.carros ?? [];
		return carros.some((c) => c.carro_id === carroId);
	});
	if (local) return { nro_viaje: null, local: true };
	const fila = enPlaya.find((f) => f.carro_id === carroId);
	return fila ? { nro_viaje: fila.nro_viaje, local: false } : null;
}
